import React, { useState, useEffect, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { uuid } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ExternalLink, FolderOpen, FileText, Upload, Link2, Loader2, 
  AlertCircle, User, Calendar, Download, Check, X, RotateCcw, Eye 
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DOCUMENT_CATEGORIES, getCategoryLabel } from '@/lib/constants/documentCategories';

const DOCUMENT_ACTOR_PROVIDER = "app";

interface CaseContact {
  contact_id: string | null;
  role: string;
  slot_label?: string | null;
  contact?: {
    id: string;
    first_name: string;
    last_name?: string | null;
  } | null;
}

interface CaseDocumentsTabProps {
  caseId: string;
  googleDriveUrl?: string | null;
  caseContacts?: CaseContact[];
  salesItemConfig?: {
    primary_person_label?: string;
    secondary_person_label?: string;
    dependent_label?: string;
  } | null;
}

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300"
};

// Helper: Sanitize filename
const sanitizeFileName = (name: string): string => {
  return name
    .normalize('NFKC')
    .replace(/\s+/g, '_')
    .replace(/[^\w.\-()]/g, '_');
};

// Helper: Normalize storage key and detect bucket
const normalizeKeyAndBucket = (k: string | null): { key: string; bucket: string } => {
  if (!k) return { key: '', bucket: 'documents' };
  const key = k.startsWith('documents/') ? k.slice('documents/'.length) : k;
  return { key, bucket: 'documents' };
};

// Helper: Detect content type
const detectType = (row: any): string => {
  const t = (row?.content_type || '').toLowerCase();
  if (t) return t;
  const name = (row?.display_name || row?.original_filename || '').toLowerCase();
  if (/\.(png|jpg|jpeg|gif|webp|avif)$/.test(name)) return 'image/*';
  if (/\.pdf$/.test(name)) return 'application/pdf';
  if (/\.(mp4|webm|mov)$/.test(name)) return 'video/*';
  if (/\.(mp3|wav|m4a|ogg)$/.test(name)) return 'audio/*';
  if (/\.(txt|csv|json|md|log)$/.test(name)) return 'text/plain';
  return 'application/octet-stream';
};

interface DocumentRow {
  id: string;
  storage_path: string | null;
  display_name: string | null;
  original_filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  category: string | null;
  status: string | null;
  uploaded_by_display_name: string | null;
  uploaded_by_email: string | null;
  reviewed_by_display_name: string | null;
  reviewed_by_email: string | null;
  reject_reason: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  // New fields for linked documents
  link_type?: string | null;
  linked_via?: string | null;
  linked_contact_id?: string | null;
  linked_contact_name?: string | null;
}

interface PreviewState {
  row: DocumentRow;
  url: string;
  type: string;
  _cleanup?: () => void;
}

export default function CaseDocumentsTab({ caseId, googleDriveUrl, caseContacts = [], salesItemConfig }: CaseDocumentsTabProps) {
  const { t } = useTranslation();
  const { data: currentUser } = useCurrentAppUser();
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [category, setCategory] = useState("");
  const [belongsTo, setBelongsTo] = useState<string>("all"); // "all" | contact_id
  const [isUploading, setIsUploading] = useState(false);

  // Categories that require a specific person
  const PERSON_REQUIRED_CATEGORIES = ['identity_civil', 'eligibility'];

  // Get people options for the dropdown
  const personOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [
      { value: 'all', label: 'Todos os envolvidos (evidência geral)' }
    ];

    const getRoleLabel = (role: string) => {
      if (!salesItemConfig) {
        if (role === 'applicant') return 'Applicant';
        if (role === 'beneficiary') return 'Beneficiary';
        if (role === 'dependent') return 'Dependent';
        return role;
      }
      if (role === 'applicant') return salesItemConfig.primary_person_label || 'Applicant';
      if (role === 'beneficiary') return salesItemConfig.secondary_person_label || 'Beneficiary';
      if (role === 'dependent') return salesItemConfig.dependent_label || 'Dependent';
      return role;
    };

    caseContacts
      .filter(cc => cc.contact && cc.role !== 'payer' && cc.role !== 'point_of_contact')
      .forEach(cc => {
        if (cc.contact_id && cc.contact) {
          const name = `${cc.contact.first_name || ''} ${cc.contact.last_name || ''}`.trim();
          const roleLabel = getRoleLabel(cc.role);
          options.push({
            value: cc.contact_id,
            label: `${name} (${roleLabel})`
          });
        }
      });

    return options;
  }, [caseContacts, salesItemConfig]);

  // Action states
  const [actionInProgress, setActionInProgress] = useState<{ docId: string; action: string } | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingDoc, setRejectingDoc] = useState<DocumentRow | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  // Preview state
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (caseId) {
      loadDocuments();
    } else {
      setIsLoading(false);
    }
  }, [caseId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preview) {
        document.body.style.overflow = '';
      }
    };
  }, [preview]);

  const loadDocuments = async () => {
    console.log("[Case/Documents] Loading documents for case:", caseId);
    setIsLoading(true);

    try {
      // Use the new view that includes linked documents
      const { data, error } = await supabase
        .from('v_case_documents_complete')
        .select(`
          document_id,
          storage_path,
          display_name,
          original_filename,
          content_type,
          size_bytes,
          category,
          status,
          uploaded_by_display_name,
          uploaded_by_email,
          reviewed_by_display_name,
          reviewed_by_email,
          reject_reason,
          reviewed_at,
          created_at,
          link_type,
          linked_via,
          linked_contact_id,
          linked_contact_name
        `)
        .eq('case_id', caseId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error("[Case/Documents] ❌ Failed to load documents:", error);
        setIsLoading(false);
        return;
      }

      // Map document_id to id for compatibility
      const mappedDocs = (data || []).map(d => ({
        ...d,
        id: d.document_id
      }));

      console.log("[Case/Documents] ✅ Loaded", mappedDocs.length, "documents (direct + linked)");
      setDocuments(mappedDocs);
    } catch (error) {
      console.error("[Case/Documents] Error loading documents:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openPreview = async (row: DocumentRow) => {
    setPreviewLoading(true);

    const { key, bucket } = normalizeKeyAndBucket(row.storage_path);
    if (!key) {
      console.error('[Case/Documents] missing storage_path');
      toast.error('Não é possível visualizar: caminho do arquivo não encontrado');
      setPreviewLoading(false);
      return;
    }

    console.log('[Case/Documents] Generating preview URL for:', key, 'from bucket:', bucket);

    try {
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from(bucket)
        .createSignedUrl(key, 3600);

      if (signedUrlError) {
        console.error('[Case/Documents] Failed to generate signed URL:', signedUrlError);
        toast.error('Falha ao gerar URL de visualização');
        setPreviewLoading(false);
        return;
      }

      const url = signedUrlData.signedUrl;
      const type = detectType(row);

      console.log('[Case/Documents] Preview URL generated');

      setPreview({ row, url, type });
      if (window.innerWidth < 768) {
        document.body.style.overflow = 'hidden';
      }

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closePreview();
      };
      document.addEventListener('keydown', handleEscape);
      setPreview(p => p ? { ...p, _cleanup: () => document.removeEventListener('keydown', handleEscape) } : null);

    } catch (error) {
      console.error('[Case/Documents] Preview error:', error);
      toast.error('Erro ao gerar visualização');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreview(prev => {
      if (prev?._cleanup) prev._cleanup();
      return null;
    });
    document.body.style.overflow = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log("[Case/Documents] File selected:", file.name, file.size, "bytes");
      setUploadFile(file);
      setDocName(file.name);
    }
  };

  const handleDownload = async (doc: DocumentRow) => {
    console.log("[Case/Documents] Downloading document:", doc.id);

    if (!doc.storage_path) {
      console.error("[Case/Documents] ❌ Document has no storage_path:", doc.id);
      toast.error("Não é possível baixar: documento sem arquivo");
      return;
    }

    try {
      const { key, bucket } = normalizeKeyAndBucket(doc.storage_path);

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(key, 60);

      if (error) {
        console.error("[Case/Documents] ❌ Failed to generate download URL:", error);
        toast.error("Falha ao gerar link de download");
        return;
      }

      console.log('[Case/Documents] Opening download URL');

      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = doc.display_name || doc.original_filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error("[Case/Documents] ❌ Download error:", error);
      toast.error("Erro ao baixar documento");
    }
  };

  const handleReviewDocument = async (doc: DocumentRow, action: string, reason: string | null = null) => {
    console.log("[Case/Documents] Reviewing document:", doc.id, "action:", action);

    if (!currentUser) {
      console.error("[Case/Documents] ❌ Current user not loaded");
      toast.error("Informações do usuário não disponíveis");
      return;
    }

    setActionInProgress({ docId: doc.id, action });

    try {
      const { data, error } = await supabase.rpc('review_document_actor', {
        p_document_id: doc.id,
        p_action: action,
        p_reason: reason || null,
        p_provider: DOCUMENT_ACTOR_PROVIDER,
        p_external_user_id: String(currentUser?.id || "unknown"),
        p_email: String(currentUser?.email || "unknown@local"),
        p_first_name: String(currentUser?.first_name || "Unknown"),
        p_last_name: String(currentUser?.last_name || "")
      });

      if (error) {
        console.error("[Case/Documents] ❌ Review RPC failed:", error);
        toast.error(`Falha ao ${action === 'approved' ? 'aprovar' : action === 'rejected' ? 'rejeitar' : 'resetar'} documento`);
        return;
      }

      console.log("[Case/Documents] ✅ Document reviewed:", data);

      const actionText = action === 'approved' ? 'aprovado' : action === 'rejected' ? 'rejeitado' : 'resetado para pendente';
      toast.success(`Documento ${actionText} com sucesso`);

      await loadDocuments();

    } catch (error: any) {
      console.error("[Case/Documents] ❌ Review error:", error);
      toast.error("Erro ao revisar documento: " + error.message);
    } finally {
      setActionInProgress(null);
      setShowRejectDialog(false);
      setRejectingDoc(null);
      setRejectReason("");
    }
  };

  const initiateReject = (doc: DocumentRow) => {
    setRejectingDoc(doc);
    setRejectReason("");
    setShowRejectDialog(true);
  };

  const confirmReject = () => {
    if (rejectingDoc) {
      handleReviewDocument(rejectingDoc, 'rejected', rejectReason || null);
    }
  };

  const handleUpload = async () => {
    console.log("[Case/Documents] === STARTING UPLOAD ===");

    if (!caseId) {
      console.error("[Case/Documents] ❌ Case ID not found");
      toast.error("ID do processo não encontrado");
      return;
    }

    if (!uploadFile) {
      console.error("[Case/Documents] ❌ No file selected");
      toast.error("Nenhum arquivo selecionado");
      return;
    }

    if (!docName || !category) {
      console.error("[Case/Documents] ❌ Missing required fields");
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    // Validate person-specific categories require a specific person
    const isPersonRequired = PERSON_REQUIRED_CATEGORIES.includes(category);
    if (isPersonRequired && belongsTo === 'all') {
      console.error("[Case/Documents] ❌ Person selection required for this category");
      toast.error("Para esta categoria, selecione a pessoa específica a quem o documento pertence");
      return;
    }

    if (!currentUser) {
      console.error("[Case/Documents] ❌ Current user not loaded");
      toast.error("Informações do usuário não disponíveis");
      return;
    }

    setIsUploading(true);

    try {
      const docId = uuid();
      const safeName = sanitizeFileName(uploadFile.name);
      const path = `cases/${caseId}/${docId}/original/${safeName}`;

      console.log("[Case/Documents] Uploading", { caseId, docId, safeName, path, fileSize: uploadFile.size, belongsTo, category });

      // STEP A: Upload to Storage
      console.log("[Case/Documents] Step A: Uploading to Storage...");

      const { data: storageData, error: storageError } = await supabase.storage
        .from('documents')
        .upload(path, uploadFile, {
          contentType: uploadFile.type || 'application/octet-stream',
          upsert: false
        });

      if (storageError) {
        console.error("[Case/Documents] ❌ Upload failed:", storageError);
        toast.error(`Falha no upload: ${storageError.message}`);
        setIsUploading(false);
        return;
      }

      console.log("[Case/Documents] ✅ Upload successful:", storageData);

      // Determine owner_contact_id based on category and belongsTo
      const ownerContactId = isPersonRequired && belongsTo !== 'all' ? belongsTo : null;

      // STEP B: Save metadata via RPC
      console.log("[Case/Documents] Step B: Saving metadata via RPC...");

      const { data: rpcData, error: rpcError } = await supabase.rpc('create_document_record_actor', {
        p_provider: DOCUMENT_ACTOR_PROVIDER,
        p_external_user_id: String(currentUser?.id || "unknown"),
        p_email: String(currentUser?.email || "unknown@local"),
        p_first_name: String(currentUser?.first_name || "Unknown"),
        p_last_name: String(currentUser?.last_name || ""),
        p_doc: {
          storage_path: path,
          owner_id: caseId,
          owner_type: "case",
          owner_contact_id: ownerContactId,
          owner_case_id: caseId,
          original_filename: safeName,
          name: docName.trim(),
          category: category,
          content_type: uploadFile.type || null,
          size_bytes: uploadFile.size,
          uploaded_via: "case_tab"
        }
      });

      if (rpcError) {
        console.error("[Case/Documents] ❌ RPC failed:", rpcError);
        toast.error(`Falha ao salvar metadados: ${rpcError.message}`);
        setIsUploading(false);
        return;
      }

      const newDocId = (rpcData as any)?.document_id || (rpcData as any)?.id;
      console.log("[Case/Documents] ✅ Metadata saved, doc ID:", newDocId);

      // STEP C: Create document_case_links based on business rules
      if (newDocId) {
        console.log("[Case/Documents] Step C: Creating document links...");

        if (isPersonRequired && belongsTo !== 'all') {
          // Person-specific document: link only to the selected contact
          console.log("[Case/Documents] Person-specific category, linking to contact:", belongsTo);
          
          // Create case link with linked_contact_id
          const { error: linkError } = await supabase.from('case_document_refs').insert({
            document_id: newDocId,
            case_id: caseId,
            linked_contact_id: belongsTo,
            source: 'case_upload_person'
          });

          if (linkError) {
            console.error("[Case/Documents] ❌ Failed to create case_document_refs link:", linkError);
          } else {
            console.log("[Case/Documents] ✅ Link created for contact:", belongsTo);
          }

        } else {
          // General document: link to all contacts in the case
          const uniqueContactIds = [...new Set(
            caseContacts
              .filter(cc => cc.contact_id && cc.role !== 'payer' && cc.role !== 'point_of_contact')
              .map(cc => cc.contact_id as string)
          )];

          console.log("[Case/Documents] General category, linking to all contacts:", uniqueContactIds);

          // Create a case link for each contact
          for (const contactId of uniqueContactIds) {
            const { error: linkError } = await supabase.from('case_document_refs').insert({
              document_id: newDocId,
              case_id: caseId,
              linked_contact_id: contactId,
              source: 'case_upload_all'
            });

            if (linkError) {
              console.error("[Case/Documents] ❌ Failed to create link for contact:", contactId, linkError);
            } else {
              console.log("[Case/Documents] ✅ Link created for contact:", contactId);
            }
          }
        }
      }

      console.log("[Case/Documents] ✅ Upload complete!");
      toast.success("Documento enviado com sucesso!");

      // Clear inputs
      setUploadFile(null);
      setDocName("");
      setCategory("");
      setBelongsTo("all");
      const fileInput = document.getElementById('case-upload-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Refresh documents table
      await loadDocuments();

    } catch (error: any) {
      console.error("[Case/Documents] ❌ Unexpected error:", error);
      toast.error(`Erro inesperado: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  if (!caseId) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            ID do processo não encontrado. Não é possível carregar ou enviar documentos.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      {/* Google Drive Link */}
      {googleDriveUrl && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{t('caseDocuments.googleDriveFolder')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('caseDocuments.accessDocuments')}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => window.open(googleDriveUrl, '_blank')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('caseDocuments.openInGoogleDrive')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Upload Card */}
      <Card className="border-2 border-indigo-200 bg-indigo-50/30 dark:border-indigo-800 dark:bg-indigo-950/20">
        <CardContent className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
              <Upload className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 mb-1">Enviar Documento</h3>
              <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
                Faça upload de documentos para este processo
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="case-upload-file-input" className="text-indigo-900 dark:text-indigo-100">Selecionar Arquivo *</Label>
                  <Input
                    id="case-upload-file-input"
                    type="file"
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="mt-1 bg-white dark:bg-slate-900 cursor-pointer"
                  />
                </div>

                <div>
                  <Label htmlFor="case-doc-name" className="text-indigo-900 dark:text-indigo-100">Nome do Documento</Label>
                  <Input
                    id="case-doc-name"
                    value={docName}
                    onChange={(e) => setDocName(e.target.value)}
                    placeholder="ex: Cópia do Passaporte"
                    disabled={isUploading}
                    className="mt-1 bg-white dark:bg-slate-900"
                  />
                </div>

                <div>
                  <Label htmlFor="case-category" className="text-indigo-900 dark:text-indigo-100">Categoria *</Label>
                  <Select 
                    value={category} 
                    onValueChange={setCategory}
                    disabled={isUploading}
                  >
                    <SelectTrigger className="mt-1 bg-white dark:bg-slate-900">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Belongs To field - only show for person-specific categories */}
                {PERSON_REQUIRED_CATEGORIES.includes(category) && (
                  <div className="md:col-span-2">
                    <Label htmlFor="case-belongs-to" className="text-indigo-900 dark:text-indigo-100">
                      Pertence a *
                    </Label>
                    <Select 
                      value={belongsTo} 
                      onValueChange={setBelongsTo}
                      disabled={isUploading}
                    >
                      <SelectTrigger className="mt-1 bg-white dark:bg-slate-900">
                        <SelectValue placeholder="Selecione a pessoa" />
                      </SelectTrigger>
                      <SelectContent>
                        {personOptions.filter(opt => opt.value !== 'all').map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {belongsTo === 'all' && (
                      <Alert className="mt-2 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-700 dark:text-amber-300 text-sm">
                          Selecione a pessoa a quem este documento pertence.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={handleUpload}
                disabled={!uploadFile || !category || isUploading || (PERSON_REQUIRED_CATEGORIES.includes(category) && belongsTo === 'all')}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Enviar e Salvar
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4">Documentos Recentes</h2>

        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2" />
            <p className="text-slate-500">Carregando documentos...</p>
          </div>
        ) : documents.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-600 dark:text-slate-400 mb-2">Nenhum documento encontrado</p>
              <p className="text-sm text-slate-500">Faça o upload do seu primeiro documento acima</p>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Nome do Documento
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Enviado Por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Revisado Por
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {documents.map((doc) => {
                    const fileName = doc.display_name || doc.original_filename || doc.storage_path?.split('/').pop() || '(sem nome)';
                    const isActionRunning = actionInProgress?.docId === doc.id;

                    return (
                      <tr key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                {fileName}
                              </span>
                              {/* Show link origin badge */}
                              {doc.link_type === 'linked' && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                  <Link2 className="w-3 h-3" />
                                  {doc.linked_via === 'identity_auto' || doc.linked_via === 'contact_sync' 
                                    ? `Identidade - ${doc.linked_contact_name || 'Contato'}`
                                    : `Via ${doc.linked_contact_name || 'Contato'}`
                                  }
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {doc.category ? (
                            <Badge variant="outline" className="text-xs">
                              {getCategoryLabel(doc.category)}
                            </Badge>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {doc.status === 'rejected' && doc.reject_reason ? (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <Badge className={`${statusColors[doc.status]} border-0 text-xs`}>
                                      {doc.status}
                                      <AlertCircle className="w-3 h-3 ml-1 inline" />
                                    </Badge>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="text-sm"><strong>Motivo:</strong> {doc.reject_reason}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <Badge className={`${statusColors[doc.status || 'pending'] || 'bg-slate-100 text-slate-700'} border-0 text-xs`}>
                              {doc.status || 'pending'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-700 dark:text-slate-300">
                              {doc.uploaded_by_display_name || 'Desconhecido'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {doc.reviewed_by_display_name ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3 text-slate-400" />
                                <span className="text-sm text-slate-700 dark:text-slate-300">
                                  {doc.reviewed_by_display_name}
                                </span>
                              </div>
                              {doc.reviewed_at && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-slate-400" />
                                  <span className="text-xs text-slate-500">
                                    {safeFormat(new Date(doc.reviewed_at), "dd/MM/yyyy")}
                                  </span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              {doc.created_at ? safeFormat(new Date(doc.created_at), "dd/MM/yyyy HH:mm") : 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openPreview(doc)}
                              disabled={isActionRunning || !doc.storage_path || previewLoading}
                              className="h-8 px-2 text-slate-600 hover:text-indigo-600"
                              title="Visualizar"
                            >
                              {previewLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>

                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownload(doc)}
                              disabled={isActionRunning}
                              className="h-8 px-2 text-slate-600 hover:text-blue-600"
                              title="Baixar"
                            >
                              <Download className="w-4 h-4" />
                            </Button>

                            {doc.status !== 'approved' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReviewDocument(doc, 'approved')}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-green-600"
                                title="Aprovar"
                              >
                                {isActionRunning && actionInProgress.action === 'approved' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </Button>
                            )}

                            {doc.status !== 'rejected' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => initiateReject(doc)}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-red-600"
                                title="Rejeitar"
                              >
                                {isActionRunning && actionInProgress.action === 'rejected' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <X className="w-4 h-4" />
                                )}
                              </Button>
                            )}

                            {(doc.status === 'approved' || doc.status === 'rejected') && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReviewDocument(doc, 'reset')}
                                disabled={isActionRunning}
                                className="h-8 px-2 text-slate-600 hover:text-amber-600"
                                title="Resetar para Pendente"
                              >
                                {isActionRunning && actionInProgress.action === 'reset' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejeitar Documento</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="case-reject-reason" className="mb-2 block">
              Motivo (opcional)
            </Label>
            <Textarea
              id="case-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Informe o motivo da rejeição..."
              className="h-24"
              disabled={actionInProgress?.action === 'rejected'}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectingDoc(null);
                setRejectReason("");
              }}
              disabled={actionInProgress?.action === 'rejected'}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmReject}
              className="bg-red-600 hover:bg-red-700"
              disabled={actionInProgress?.action === 'rejected'}
            >
              {actionInProgress?.action === 'rejected' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rejeitando...
                </>
              ) : (
                "Rejeitar Documento"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {preview && (
        <div
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closePreview(); }}
        >
          <div style={{
            background: '#fff',
            borderRadius: 12,
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: 'min(1000px,90vw)',
            boxShadow: '0 10px 30px rgba(0,0,0,.2)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <header style={{
              padding: '16px 20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '16px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '8px'
                }} title={preview.row?.display_name || preview.row?.original_filename || 'Documento'}>
                  {preview.row?.display_name || preview.row?.original_filename || 'Documento'}
                </div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#64748b' }}>
                  {preview.row?.category && (
                    <div className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      <span>{preview.row.category}</span>
                    </div>
                  )}

                  {preview.row?.size_bytes && (
                    <span>
                      {(preview.row.size_bytes / 1024).toFixed(1)} KB
                    </span>
                  )}

                  {preview.row?.uploaded_by_display_name && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span>por {preview.row.uploaded_by_display_name}</span>
                    </div>
                  )}

                  <Badge className={`${statusColors[preview.row?.status || 'pending']} text-xs`}>
                    {preview.row?.status || 'pending'}
                  </Badge>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(preview.row)}
                  title="Baixar"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Baixar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={closePreview}
                  title="Fechar"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </header>

            <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
              {preview.type.startsWith('image/') ? (
                <img
                  src={preview.url}
                  alt={preview.row?.display_name || 'Imagem'}
                  style={{ maxWidth: '100%', height: 'auto', margin: '0 auto', display: 'block' }}
                />
              ) : preview.type === 'application/pdf' ? (
                <iframe
                  src={preview.url}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                  title={preview.row?.display_name || 'PDF'}
                />
              ) : preview.type.startsWith('video/') ? (
                <video
                  src={preview.url}
                  controls
                  style={{ maxWidth: '100%', height: 'auto', margin: '0 auto', display: 'block' }}
                />
              ) : preview.type.startsWith('audio/') ? (
                <audio
                  src={preview.url}
                  controls
                  style={{ width: '100%', margin: '20px auto', display: 'block' }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">Visualização não disponível para este tipo de arquivo</p>
                  <Button onClick={() => handleDownload(preview.row)}>
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Arquivo
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
